import apiClient from './apiClient';

// Authentication API Services
export const authAPI = {
  // Login user
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/user/login', credentials);
      if (response.data.success && response.data.user) {
        const u = response.data.user;
        // Check if farmer or processor account is pending approval
        if (['farmer', 'processor'].includes(u.role) && u.status === 'pending') {
          throw new Error('⚠️ Account Pending Admin Approval: An administrator must review and approve your registration before you can log in.');
        }

        if (response.data.token) {
          sessionStorage.setItem('bciis_auth_token', response.data.token);
        }
        sessionStorage.setItem('bciis_user', JSON.stringify(u));
        return response.data;
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      // Check local user applications fallback
      const username = credentials.username?.toLowerCase();
      const apps = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
      const pendingApp = apps.find(a => (a.username?.toLowerCase() === username || a.email?.toLowerCase() === username) && a.status === 'pending');
      if (pendingApp) {
        throw new Error('⚠️ Account Pending Admin Approval: Your Farmer/Processor account registration is pending review by an administrator.');
      }
      throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || 'Login failed');
    }
  },

  // Register new user
  signup: async (userData) => {
    const isMemberAccount = ['farmer', 'processor'].includes(userData.role);
    try {
      const response = await apiClient.post('/user/register', {
        ...userData,
        status: isMemberAccount ? 'pending' : 'active'
      });

      if (response.data.success) {
        const userId = response.data.userId || Date.now();
        const user = {
          id: userId,
          username: userData.username,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          organization: userData.organization,
          municipality: userData.municipality,
          role: userData.role || 'user',
          status: isMemberAccount ? 'pending' : 'active'
        };

        if (isMemberAccount) {
          // Store application for Admin PendingApplications view
          const apps = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
          apps.unshift({
            id: userId,
            userId,
            fullname: `${userData.firstName} ${userData.lastName}`,
            username: userData.username,
            email: userData.email,
            role: userData.role,
            municipality: userData.municipality,
            organization: userData.organization,
            status: 'pending',
            submittedAt: new Date().toISOString()
          });
          localStorage.setItem('bciis_user_applications', JSON.stringify(apps));

          // Notify Admin
          notificationsAPI.sendNotification({
            role: 'admin',
            title: 'New Account Application',
            message: `New ${userData.role === 'farmer' ? 'Farmer' : 'Processor'} account registration from ${userData.firstName} ${userData.lastName} (${userData.username}) is pending approval.`,
            type: 'request'
          }).catch(() => {});

          return {
            success: true,
            isPendingApproval: true,
            message: `Registration successful! Your ${userData.role === 'farmer' ? 'Farmer' : 'Processor'} account application is pending Admin approval.`,
            user
          };
        } else {
          sessionStorage.setItem('bciis_user', JSON.stringify(user));
          return { success: true, message: response.data.message, user };
        }
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      if (isMemberAccount) {
        // Fallback for offline/local environment
        const userId = Date.now();
        const user = {
          id: userId,
          username: userData.username,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          organization: userData.organization,
          municipality: userData.municipality,
          role: userData.role || 'user',
          status: 'pending'
        };

        const apps = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
        apps.unshift({
          id: userId,
          userId,
          fullname: `${userData.firstName} ${userData.lastName}`,
          username: userData.username,
          email: userData.email,
          role: userData.role,
          municipality: userData.municipality,
          organization: userData.organization,
          status: 'pending',
          submittedAt: new Date().toISOString()
        });
        localStorage.setItem('bciis_user_applications', JSON.stringify(apps));

        // Save local users
        const users = JSON.parse(localStorage.getItem('bciis_users') || '[]');
        users.push(user);
        localStorage.setItem('bciis_users', JSON.stringify(users));

        // Notify Admin
        notificationsAPI.sendNotification({
          role: 'admin',
          title: 'New Account Application',
          message: `New ${userData.role === 'farmer' ? 'Farmer' : 'Processor'} account registration from ${userData.firstName} ${userData.lastName} (${userData.username}) is pending approval.`,
          type: 'request'
        }).catch(() => {});

        return {
          success: true,
          isPendingApproval: true,
          message: `Registration successful! Your ${userData.role === 'farmer' ? 'Farmer' : 'Processor'} account application is pending Admin approval.`,
          user
        };
      }
      throw new Error(error.response?.data?.message || error.response?.data?.error || error.message || 'Registration failed');
    }
  },

  // Logout user
  logout: async () => {
    try {
      await apiClient.post('/user/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      // Always clear session storage
      sessionStorage.removeItem('bciis_auth_token');
      sessionStorage.removeItem('bciis_user');
      sessionStorage.removeItem('bciis_current_page');
    }
  },

  // Verify token validity
  verifyToken: async () => {
    try {
      const response = await apiClient.post('/auth/authCheck', {
        session: sessionStorage.getItem('bciis_auth_token')
      });
      return response.data;
    } catch (error) {
      throw new Error('Token verification failed');
    }
  },

  // Refresh token (not implemented on backend, kept for future use)
  refreshToken: async () => {
    try {
      const response = await apiClient.post('/user/login');
      if (response.data.token) {
        sessionStorage.setItem('bciis_auth_token', response.data.token);
      }
      return response.data;
    } catch (error) {
      throw new Error('Token refresh failed');
    }
  },

  // Get current user profile (stored locally; no dedicated backend GET profile endpoint)
  getProfile: async () => {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
      return { success: true, user: currentUser };
    } catch (error) {
      throw new Error('Failed to fetch profile');
    }
  },

  // Forgot password - request reset code
  forgotPassword: async (email) => {
    try {
      const response = await apiClient.post('/user/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to send reset code');
    }
  },

  // Verify reset code
  verifyResetCode: async (email, resetCode) => {
    try {
      const response = await apiClient.post('/user/verify-reset-code', { email, resetCode });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Invalid or expired reset code');
    }
  },

  // Reset password using verified code
  resetPassword: async (email, resetCode, newPassword) => {
    try {
      const response = await apiClient.post('/user/reset-password', { email, resetCode, newPassword });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to reset password');
    }
  },

  // Update user profile
  updateProfile: async (profileData) => {
    try {
      // Get user ID from session storage or profileData
      const currentUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
      const userId = profileData.id || profileData.user_id || currentUser.id || currentUser.user_id;
      if (!userId) {
        throw new Error('User ID not found. Please log in again.');
      }

      const requestData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        organization: profileData.organization,
        municipality: profileData.municipality,
        avatar: profileData.avatar
      };

      // Log avatar information
      // Avatar data included in request
      const response = await apiClient.put(`/user/${userId}/profile`, requestData);
      // Update session storage with new user data
      if (response.data.success) {
        const updatedUser = {
          ...currentUser,
          ...profileData
        };
        sessionStorage.setItem('bciis_user', JSON.stringify(updatedUser));
        return { success: true, user: updatedUser };
      } else {
      }
      
      return response.data;
    } catch (error) {
      
      // Check for specific error types
      if (error.response?.status === 413) {
        throw new Error('Profile picture is too large. Please use a smaller image.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error('Failed to update profile. Please try again.');
      }
    }
  },

  // Change password
  changePassword: async (passwordData) => {
    try {
      const response = await apiClient.put(`/user/${passwordData.userId}/password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to change password');
    }
  },

  // Upload user avatar (avatar is stored as base64 via updateProfile; kept for future use)
  uploadAvatar: async (avatarFile) => {
    try {
      const formData = new FormData();
      formData.append('avatar', avatarFile);
      
      const response = await apiClient.post('/auth/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update session storage with new avatar URL
      const currentUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
      if (response.data.avatarUrl) {
        currentUser.avatar = response.data.avatarUrl;
        sessionStorage.setItem('bciis_user', JSON.stringify(currentUser));
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload avatar');
    }
  }
};

const normalizeAdminGender = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'm' || normalized === 'male') return 'm';
  if (normalized === 'f' || normalized === 'female') return 'f';
  return '';
};

// Admin API Services
export const adminAPI = {
  // Admin login
  login: async (credentials) => {
    try {
      // Backend admin login uses POST with JSON body
      const response = await apiClient.post('/admins/login', {
        username: credentials.username,
        password: credentials.password
      });
      
      // Backend returns { success, message, token, user }
      if (response.data && response.data.success && response.data.token) {
        const adminData = response.data.user;
        
        // Create a standardized user object
        const user = {
          id: adminData.id,
          username: adminData.username || credentials.username,
          fullname: adminData.fullname || '',
          gender: normalizeAdminGender(adminData.gender ?? adminData.Gender) || 'm',
          role: 'admin',
          avatar: adminData.avatar || null
        };
        
        // Store auth data with real JWT from backend
        sessionStorage.setItem('bciis_auth_token', response.data.token);
        sessionStorage.setItem('bciis_user', JSON.stringify(user));
        
        return { 
          success: true, 
          message: 'Login successful', 
          token: response.data.token, 
          user: user 
        };
      } else {
        throw new Error('Invalid admin credentials');
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Admin login failed');
    }  },

  // Update admin profile
  updateProfile: async (adminId, profileData) => {
    try {
      const response = await apiClient.put(`/admins/${adminId}/profile`, profileData);
      if (response.data.success) {
        // Update stored user data in sessionStorage
        const storedUser = JSON.parse(sessionStorage.getItem('bciis_user') || '{}');
        const updatedUser = {
          ...storedUser,
          fullname: profileData.fullname,
          gender: normalizeAdminGender(profileData.gender) || 'm',
          avatar: profileData.avatar
        };
        sessionStorage.setItem('bciis_user', JSON.stringify(updatedUser));
        
        return response.data;
      } else {
        throw new Error(response.data.error || 'Failed to update admin profile');
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Failed to update admin profile');
    }  }
  ,

  // Change admin password
  changePassword: async (adminId, passwordData) => {
    try {
      const response = await apiClient.put(`/admins/${adminId}/password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to change password');
    }
  }
};

// Admin Application API Services
export const adminApplicationAPI = {
  // Submit new admin application
  apply: async (applicationData) => {
    try {
      const response = await apiClient.post('/admin-applications/apply', applicationData);
      if (response.data.error === false) {
        return {
          success: true,
          message: response.data.message,
          applicationId: response.data.applicationId
        };
      } else {
        throw new Error(response.data.message || 'Application submission failed');
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to submit admin application');
    }
  },

  // Get all pending applications (Admin only)
  getPending: async () => {
    try {
      const response = await apiClient.get('/admin-applications/pending');
      
      if (response.data.error === false) {
        return {
          success: true,
          applications: response.data.applications || []
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch pending applications');
      }
    } catch (error) {
      const stored = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
      const pending = stored.filter(a => a.status === 'pending');
      return { success: true, applications: pending };
    }
  },

  // Get all applications (Admin only)
  getAll: async () => {
    try {
      const response = await apiClient.get('/admin-applications/all');
      
      if (response.data.error === false) {
        return {
          success: true,
          applications: response.data.applications || []
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch applications');
      }
    } catch (error) {
      const stored = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
      return { success: true, applications: stored };
    }
  },

  // Get application by ID
  getById: async (applicationId) => {
    try {
      const response = await apiClient.get(`/admin-applications/${applicationId}`);
      
      if (response.data.error === false) {
        return {
          success: true,
          application: response.data.application
        };
      } else {
        throw new Error(response.data.message || 'Application not found');
      }
    } catch (error) {
      const stored = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
      const found = stored.find(a => String(a.id) === String(applicationId));
      return { success: true, application: found || null };
    }
  },

  // Approve an admin application
  approve: async (applicationId, reviewedBy) => {
    try {
      const response = await apiClient.put(`/admin-applications/${applicationId}/approve`, {
        reviewedBy
      });
      
      if (response.data.error === false) {
        return {
          success: true,
          message: response.data.message,
          adminId: response.data.adminId
        };
      } else {
        throw new Error(response.data.message || 'Approval failed');
      }
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
      const item = stored.find(a => String(a.id) === String(applicationId));
      if (item) {
        item.status = 'approved';
        item.reviewedBy = reviewedBy;
        item.reviewedAt = new Date().toISOString();
        localStorage.setItem('bciis_user_applications', JSON.stringify(stored));

        // Update user status in localStorage user list
        let users = JSON.parse(localStorage.getItem('bciis_users') || '[]');
        const uIdx = users.findIndex(u => String(u.id) === String(item.userId || item.id));
        if (uIdx >= 0) {
          users[uIdx].status = 'approved';
          localStorage.setItem('bciis_users', JSON.stringify(users));
        }

        // Notify User
        notificationsAPI.sendNotification({
          userId: item.userId || item.id,
          title: 'Account Registration Approved!',
          message: `Your ${item.role === 'farmer' ? 'Farmer' : item.role === 'processor' ? 'Processor' : 'Member'} account application has been approved by Admin. You may now log in!`,
          type: 'approval'
        }).catch(() => {});

        // Notify Admin
        notificationsAPI.sendNotification({
          role: 'admin',
          title: 'Account Registration Approved',
          message: `You approved account registration for ${item.fullname || item.username}.`,
          type: 'approval'
        }).catch(() => {});
      }
      return { success: true, message: 'Application approved successfully! User can now log in.' };
    }
  },

  // Reject an admin application
  reject: async (applicationId, reviewedBy) => {
    try {
      const response = await apiClient.put(`/admin-applications/${applicationId}/reject`, {
        reviewedBy
      });
      
      if (response.data.error === false) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        throw new Error(response.data.message || 'Rejection failed');
      }
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_user_applications') || '[]');
      const item = stored.find(a => String(a.id) === String(applicationId));
      if (item) {
        item.status = 'rejected';
        item.reviewedBy = reviewedBy;
        item.reviewedAt = new Date().toISOString();
        localStorage.setItem('bciis_user_applications', JSON.stringify(stored));

        // Notify User
        notificationsAPI.sendNotification({
          userId: item.userId || item.id,
          title: 'Account Registration Declined',
          message: `Your ${item.role} account registration application was declined by Admin.`,
          type: 'rejection'
        }).catch(() => {});

        // Notify Admin
        notificationsAPI.sendNotification({
          role: 'admin',
          title: 'Account Registration Declined',
          message: `You declined account registration for ${item.fullname || item.username}.`,
          type: 'rejection'
        }).catch(() => {});
      }
      return { success: true, message: 'Application rejected.' };
    }
  }
};

// Processors API Services
export const processorsAPI = {
  // Get all processors with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await apiClient.get(`/processors/getall${queryParams ? `?${queryParams}` : ''}`);
      return response.data;
    } catch (error) {
      // Log full axios error when available to aid debugging (500, ETIMEDOUT, etc.)
      try {
      } catch (logErr) {
      }

      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch processors');
    }
  },

  // Get processor by ID
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/processors/businesses/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch processor');
    }
  },

  // Create new processor
  create: async (processorData) => {
    try {
      const response = await apiClient.post('/processors/insert_business', processorData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create processor');
    }
  },

  // Update processor
  update: async (id, processorData) => {
    try {
      const response = await apiClient.put(`/processors/businesses/${id}`, processorData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update processor');
    }
  },

  // Delete processor
  delete: async (id) => {
    // Backend mounts processors router at /processors (from server.js: app.use("/processors", Processors))
    // The delete route is router.delete("/businesses/:id", ...) in the processors router
    // Full path: /processors/businesses/:id
    try {
      const response = await apiClient.delete(`/processors/businesses/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      // Extract backend error message
      const serverMsg = error.response?.data?.error || error.response?.data?.message;
      throw new Error(serverMsg || error.message || 'Failed to delete processor');
    }
  },

  // Update processor status (admin only)
  updateStatus: async (id, status) => {
    try {
      const response = await apiClient.patch(`/processors/${id}/status`, { statusOfBusiness: status });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update processor status');
    }
  },

  // Bulk delete processors - NOTE: no backend route exists for this yet
  // bulkDelete: async (ids) => { ... },

  // Get processors by municipality
  getByMunicipality: async (municipality) => {
    try {
      const response = await apiClient.get(`/processors/municipality/${municipality}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch processors by municipality');
    }
  },

  // Search processors
  search: async (searchTerm) => {
    try {
      const response = await apiClient.get(`/processors/search?q=${encodeURIComponent(searchTerm)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search processors');
    }
  },

  // Bulk import comprehensive processors
  bulkImportComprehensive: async (processors) => {
    try {
      const response = await apiClient.post('/processors/bulk-import-comprehensive', { processors });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Import failed');
    }
  }
};

// Farmers API Services
// NOTE: these endpoints map to the backend routes you provided. The router appears to expose
// routes like: GET /get_farmer, GET /search?q=, POST /insert, PUT /update/:id, DELETE /delete/:id
// which are assumed to be mounted under the "farmers" router path on the server (e.g. /farmers/get_farmer).
export const farmersAPI = {
  // Get all farmers (calls your stored procedure endpoint)
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await apiClient.get(`/farmers/get_farmer${queryParams ? `?${queryParams}` : ''}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch farmers');
    }
  },

  // Search farmers using backend search endpoint
  search: async (searchTerm) => {
    try {
      const response = await apiClient.get(`/farmers/search?q=${encodeURIComponent(searchTerm)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search farmers');
    }
  },

  // Insert a farmer (calls your /insert route)
  create: async (farmerData) => {
    try {
      // Sanitize payload: replace any `undefined` values with `null` so the MySQL driver
      // doesn't reject bind parameters. Also strip out any keys with value `undefined` in
      // nested objects by converting them to null or removing them as appropriate.
      // sanitize nested objects by removing undefined keys; keep null values.
      // For the top-level SP parameters we want explicit nulls for missing scalars,
      // but nested objects should be omitted or only contain provided fields.
      const sanitize = (v) => {
        if (v === undefined) return undefined; // indicate absence
        if (v === null) return null;
        if (Array.isArray(v)) return v.map(sanitize);
        if (typeof v === 'object') {
          const out = {};
          for (const [k, val] of Object.entries(v)) {
            const s = sanitize(val);
            // only include keys that are not undefined
            if (s !== undefined) out[k] = s;
          }
          return Object.keys(out).length ? out : undefined;
        }
        return v;
      };

      const replaceTopLevelUndefinedWithNull = (obj) => {
        // Ensure the main stored-procedure scalar params exist and are null when absent.
        const expectedTopLevel = [
          'lastname','firstname','middleInitial','respondent','sitioPurok',
          'barangay','municipality','age','gender','civilStatus','education','household','occupation'
        ];

        const out = {};
        const src = (obj && typeof obj === 'object' && !Array.isArray(obj)) ? obj : {};

        // Copy provided keys, converting any accidental undefined to null
        for (const [k, v] of Object.entries(src)) {
          out[k] = (v === undefined) ? null : v;
        }

        // Ensure expected top-level keys are present (set to null if missing)
        for (const k of expectedTopLevel) {
          if (!(k in out)) out[k] = null;
        }

        // Also preserve any nested objects that were present (incomeData, membershipData, etc.)
        return out;
      };

      const partiallySanitized = sanitize(farmerData);
      const sanitized = replaceTopLevelUndefinedWithNull(partiallySanitized || {});
      
      const response = await apiClient.post('/farmers/insert', sanitized);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create farmer');
    }
  },

  // Update farmer (calls your /update/:id route)
  update: async (id, farmerData) => {
    try {
      const response = await apiClient.put(`/farmers/update/${encodeURIComponent(id)}`, farmerData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update farmer');
    }
  },

  // Delete farmer (calls your /delete/:id route)
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/farmers/delete/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      // Prefer the `error` field many backend routes return, fall back to `message`.
      const serverMsg = error.response?.data?.error || error.response?.data?.message;
      throw new Error(serverMsg || error.message || 'Failed to delete farmer');
    }
  }
};

// Analytics API Services
export const analyticsAPI = {
  // Get full analytics overview (totalFarmers, processors, land area, gender/age breakdown, etc.)
  getStats: async () => {
    try {
      const response = await apiClient.get('/analytics/overview');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
    }
  }
  // Other analytics views (municipalities, trends) are computed client-side
  // from the farmers and processors data fetched via farmersAPI and processorsAPI.
};

// Utility API Services
export const utilityAPI = {
  // Get municipalities list
  getMunicipalities: async () => {
    try {
      const response = await apiClient.get('/utilities/municipalities');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch municipalities');
    }
  },

  // Upload file/image
  uploadFile: async (file, type = 'general') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await apiClient.post('/utilities/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'File upload failed');
    }
  }
};

// Team Members API Services
export const teamAPI = {
  // Get all team members
  getAll: async () => {
    try {
      const response = await apiClient.get('/team/getall');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch team members');
    }
  }
};

// Services API (platform services offered by admin)
// Helper: map frontend fields to backend fields
const toServerService = (data) => ({
  service_name: data.title ?? data.service_name ?? null,
  service_description: data.description ?? data.service_description ?? null,
  service_contact: data.contact ?? data.service_contact ?? null,
  is_active: data.active !== undefined ? (data.active ? 1 : 0) : (data.is_active !== undefined ? data.is_active : 1),
  created_by: data.created_by ?? null,
});

// Helper: map backend fields to frontend fields
const fromServerService = (row) => ({
  ...row,
  id: row.service_id ?? row.id,
  title: row.service_name ?? row.title ?? '',
  description: row.service_description ?? row.description ?? '',
  contact: row.service_contact ?? row.contact ?? '',
  active: row.is_active !== undefined ? !!row.is_active : (row.active !== undefined ? !!row.active : true),
});

export const servicesAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/services');
      const result = response.data;
      // Normalize rows from server format to frontend format
      if (result?.data && Array.isArray(result.data)) {
        result.data = result.data.map(fromServerService);
      }
      return result;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch services');
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/services/${encodeURIComponent(id)}`);
      const result = response.data;
      if (result?.data) {
        result.data = fromServerService(result.data);
      }
      return result;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch service');
    }
  },

  create: async (serviceData) => {
    try {
      const payload = toServerService(serviceData);
      const response = await apiClient.post('/services', payload);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create service');
    }
  },

  update: async (id, serviceData) => {
    try {
      const payload = toServerService(serviceData);
      const response = await apiClient.put(`/services/${encodeURIComponent(id)}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update service');
    }
  },

  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/services/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete service');
    }
  }
};

export const mapsAPI = {
  getCatalog: async (category) => {
    try {
      const response = await apiClient.get('/maps/catalog', {
        params: category ? { category } : undefined,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch maps');
    }
  },

  getOne: async (category, municipality) => {
    try {
      const response = await apiClient.get(
        `/maps/catalog/${encodeURIComponent(category)}/${encodeURIComponent(municipality)}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch map');
    }
  },

  saveCatalog: async (mapData) => {
    try {
      const response = await apiClient.post('/maps/catalog/save', mapData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to save map');
    }
  },

  deleteCatalog: async (category, id) => {
    try {
      const response = await apiClient.delete(`/maps/catalog/${encodeURIComponent(category)}/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to delete map');
    }
  },
};

export const researchAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/research');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch research papers');
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/research/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch research paper');
    }
  },

  create: async (paperData) => {
    try {
      const response = await apiClient.post('/research', paperData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to upload research paper');
    }
  },

  incrementDownload: async (id) => {
    try {
      const response = await apiClient.put(`/research/${encodeURIComponent(id)}/download`);
      return response.data;
    } catch (error) {
      // Non-critical, ignore
    }
  },

  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/research/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to delete research paper');
    }
  },
};

// ============================================================
// Profile Posting Requests API (Farmers & Processors)
// ============================================================
export const profileRequestAPI = {
  // Submit a posting request
  submit: async ({ userId, role, formData, agreedAt }) => {
    try {
      const response = await apiClient.post('/profile-requests/submit', {
        userId, role, formData, agreedAt
      });
      // Notify Admin
      notificationsAPI.sendNotification({
        role: 'admin',
        title: 'New Public Listing Request',
        message: `A ${role === 'farmer' ? 'Farmer' : 'Processor'} submitted a request for public listing approval.`,
        type: 'request'
      }).catch(() => {});
      return response.data;
    } catch (error) {
      // LocalStorage Fallback
      const stored = JSON.parse(localStorage.getItem('bciis_profile_requests') || '[]');
      let userObj = {};
      try { userObj = JSON.parse(sessionStorage.getItem('bciis_user') || '{}'); } catch {}
      const existingIndex = stored.findIndex(r => String(r.userId) === String(userId));
      const newReq = {
        id: existingIndex >= 0 ? stored[existingIndex].id : Date.now(),
        userId,
        role,
        formData,
        agreedAt,
        status: 'pending',
        submittedAt: new Date().toISOString(),
        userFullname: userObj.fullname || userObj.fullName || userObj.username || (role === 'farmer' ? `${formData.lastname || ''}, ${formData.firstname || ''}` : formData.name) || 'User',
        username: userObj.username || 'user',
        email: userObj.email || ''
      };
      if (existingIndex >= 0) {
        stored[existingIndex] = newReq;
      } else {
        stored.unshift(newReq);
      }
      localStorage.setItem('bciis_profile_requests', JSON.stringify(stored));
      // Notify Admin
      notificationsAPI.sendNotification({
        role: 'admin',
        title: 'New Public Listing Request',
        message: `A ${role === 'farmer' ? 'Farmer' : 'Processor'} submitted a request for public listing approval.`,
        type: 'request'
      }).catch(() => {});

      return {
        success: true,
        message: 'Your posting request has been submitted successfully. Please wait for admin approval.',
        requestId: newReq.id,
        status: 'pending'
      };
    }
  },

  // Get the current user's posting request status
  getMyRequest: async (userId) => {
    try {
      const response = await apiClient.get(`/profile-requests/my-request/${userId}`);
      return response.data;
    } catch (error) {
      const stored = JSON.parse(localStorage.getItem('bciis_profile_requests') || '[]');
      const found = stored.find(r => String(r.userId) === String(userId));
      return { success: true, request: found || null };
    }
  },

  // Admin: Get all posting requests (optional filter: pending/approved/rejected)
  getAll: async (status = null) => {
    try {
      const params = status ? { status } : {};
      const response = await apiClient.get('/profile-requests/all', { params });
      return response.data;
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_profile_requests') || '[]');
      if (status && status !== 'all') {
        stored = stored.filter(r => r.status === status);
      }
      return { success: true, requests: stored };
    }
  },

  // Admin: Approve a posting request
  approve: async (requestId, adminId) => {
    try {
      const response = await apiClient.put(`/profile-requests/${requestId}/approve`, { adminId });
      return response.data;
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_profile_requests') || '[]');
      const reqItem = stored.find(r => String(r.id) === String(requestId));
      if (reqItem) {
        reqItem.status = 'approved';
        reqItem.reviewedBy = adminId;
        reqItem.reviewedAt = new Date().toISOString();
        localStorage.setItem('bciis_profile_requests', JSON.stringify(stored));
        // Notify applicant
        notificationsAPI.sendNotification({
          userId: reqItem.userId,
          title: 'Public Listing Approved!',
          message: 'Your public directory listing request has been approved by the Admin.',
          type: 'approval'
        }).catch(() => {});
      }
      return { success: true, message: 'Request approved successfully.' };
    }
  },

  // Admin: Reject a posting request
  reject: async (requestId, adminId, rejectionReason = '') => {
    try {
      const response = await apiClient.put(`/profile-requests/${requestId}/reject`, {
        adminId, rejectionReason
      });
      return response.data;
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_profile_requests') || '[]');
      const reqItem = stored.find(r => String(r.id) === String(requestId));
      if (reqItem) {
        reqItem.status = 'rejected';
        reqItem.rejectionReason = rejectionReason;
        reqItem.reviewedBy = adminId;
        reqItem.reviewedAt = new Date().toISOString();
        localStorage.setItem('bciis_profile_requests', JSON.stringify(stored));
        // Notify applicant
        notificationsAPI.sendNotification({
          userId: reqItem.userId,
          title: 'Public Listing Request Rejected',
          message: `Your request was rejected by Admin. Reason: ${rejectionReason}`,
          type: 'rejection'
        }).catch(() => {});
      }
      return { success: true, message: 'Request rejected.' };
    }
  },
};

// ── Notifications API ───────────────────────────────────────────────────────
export const notificationsAPI = {
  getNotifications: async (userId, role) => {
    try {
      const response = await apiClient.get('/notifications', { params: { userId, role } });
      return response.data;
    } catch (error) {
      // Local storage fallback
      const stored = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
      const filtered = stored.filter(n => 
        (n.userId && String(n.userId) === String(userId)) ||
        (n.role && (n.role === role || n.role === 'all'))
      );
      return { success: true, notifications: filtered };
    }
  },

  sendNotification: async (data) => {
    try {
      const response = await apiClient.post('/notifications/send', data);
      return response.data;
    } catch (error) {
      const stored = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
      const newNotif = { id: Date.now(), ...data, read: false, createdAt: new Date().toISOString() };
      stored.unshift(newNotif);
      localStorage.setItem('bciis_notifications', JSON.stringify(stored));
      return { success: true, notification: newNotif };
    }
  },

  markAsRead: async (notificationId, userId) => {
    try {
      const response = await apiClient.post('/notifications/read', { notificationId, userId });
      return response.data;
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
      if (notificationId) {
        stored = stored.map(n => n.id === notificationId ? { ...n, read: true } : n);
      } else if (userId) {
        stored = stored.map(n => String(n.userId) === String(userId) ? { ...n, read: true } : n);
      }
      localStorage.setItem('bciis_notifications', JSON.stringify(stored));
      return { success: true };
    }
  },

  clearNotifications: async (userId) => {
    try {
      const response = await apiClient.post('/notifications/clear', { userId });
      return response.data;
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
      stored = stored.filter(n => String(n.userId) !== String(userId));
      localStorage.setItem('bciis_notifications', JSON.stringify(stored));
      return { success: true };
    }
  },

  deleteNotification: async (notificationId, userId) => {
    try {
      const response = await apiClient.delete(`/notifications/${notificationId}`, { params: { userId } });
      return response.data;
    } catch (error) {
      let stored = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
      stored = stored.filter(n => String(n.id) !== String(notificationId));
      localStorage.setItem('bciis_notifications', JSON.stringify(stored));
      return { success: true };
    }
  }
};

// ── Direct Messaging API ────────────────────────────────────────────────────
export const messagesAPI = {
  getDirectory: async () => {
    try {
      const response = await apiClient.get('/messages/directory');
      return response.data;
    } catch (error) {
      return { success: false, directory: [] };
    }
  },

  getConversations: async (userId) => {
    try {
      const response = await apiClient.get('/messages/conversations', { params: { userId } });
      return response.data;
    } catch (error) {
      const msgs = JSON.parse(localStorage.getItem('bciis_direct_messages') || '[]');
      const userMsgs = msgs.filter(m => String(m.senderId) === String(userId) || String(m.receiverId) === String(userId));
      const partnersMap = new Map();
      userMsgs.forEach(m => {
        const isSender = String(m.senderId) === String(userId);
        const partnerId = isSender ? m.receiverId : m.senderId;
        const partnerName = isSender ? m.receiverName : m.senderName;
        const partnerRole = isSender ? m.receiverRole : m.senderRole;
        if (!partnersMap.has(partnerId) || new Date(m.timestamp) > new Date(partnersMap.get(partnerId).lastMessageTime)) {
          partnersMap.set(partnerId, {
            partnerId, partnerName, partnerRole,
            lastMessage: m.message, lastMessageTime: m.timestamp,
            unread: !isSender && !m.read
          });
        }
      });
      return { success: true, conversations: Array.from(partnersMap.values()) };
    }
  },

  getThread: async (userId, partnerId) => {
    try {
      const response = await apiClient.get('/messages/thread', { params: { userId, partnerId } });
      return response.data;
    } catch (error) {
      let msgs = JSON.parse(localStorage.getItem('bciis_direct_messages') || '[]');
      const thread = msgs.filter(m =>
        (String(m.senderId) === String(userId) && String(m.receiverId) === String(partnerId)) ||
        (String(m.senderId) === String(partnerId) && String(m.receiverId) === String(userId))
      ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Mark read
      msgs = msgs.map(m => {
        if (String(m.senderId) === String(partnerId) && String(m.receiverId) === String(userId)) {
          return { ...m, read: true };
        }
        return m;
      });
      localStorage.setItem('bciis_direct_messages', JSON.stringify(msgs));

      return { success: true, thread };
    }
  },

  sendMessage: async (data) => {
    try {
      const response = await apiClient.post('/messages/send', data);
      return response.data;
    } catch (error) {
      const msgs = JSON.parse(localStorage.getItem('bciis_direct_messages') || '[]');
      const newMsg = { id: Date.now(), ...data, timestamp: new Date().toISOString(), read: false };
      msgs.push(newMsg);
      localStorage.setItem('bciis_direct_messages', JSON.stringify(msgs));

      // Also create a notification for receiver
      const storedNotifs = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
      storedNotifs.unshift({
        id: Date.now() + 1,
        userId: data.receiverId,
        role: data.receiverRole || 'all',
        type: 'message',
        title: `New message from ${data.senderName}`,
        message: data.message.length > 50 ? data.message.substring(0, 50) + '...' : data.message,
        read: false,
        createdAt: new Date().toISOString()
      });
      localStorage.setItem('bciis_notifications', JSON.stringify(storedNotifs));

      return { success: true, message: newMsg };
    }
  }
};

// ── Concerns & Feedback API ─────────────────────────────────────────────────
export const concernsAPI = {
  submitConcern: async (data) => {
    try {
      const response = await apiClient.post('/concerns/submit', data);
      return response.data;
    } catch (error) {
      const concerns = JSON.parse(localStorage.getItem('bciis_concerns') || '[]');
      const newConcern = {
        id: Date.now(),
        ...data,
        status: 'Pending',
        submittedAt: new Date().toISOString(),
        adminReply: null,
        repliedAt: null
      };
      concerns.unshift(newConcern);
      localStorage.setItem('bciis_concerns', JSON.stringify(concerns));

      // Create admin notification
      const storedNotifs = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
      storedNotifs.unshift({
        id: Date.now() + 1,
        role: 'admin',
        type: 'concern',
        title: `New Concern: ${data.subject}`,
        message: `${data.userName} (${data.userRole}) submitted a new concern: "${data.details.substring(0, 60)}..."`,
        read: false,
        createdAt: new Date().toISOString()
      });
      localStorage.setItem('bciis_notifications', JSON.stringify(storedNotifs));

      return {
        success: true,
        message: 'Thank you for your feedback or concern, we will be right with you!',
        concern: newConcern
      };
    }
  },

  getAllConcerns: async () => {
    try {
      const response = await apiClient.get('/concerns/all');
      return response.data;
    } catch (error) {
      const concerns = JSON.parse(localStorage.getItem('bciis_concerns') || '[]');
      return { success: true, concerns };
    }
  },

  getMyConcerns: async (userId) => {
    try {
      const response = await apiClient.get('/concerns/my', { params: { userId } });
      return response.data;
    } catch (error) {
      const concerns = JSON.parse(localStorage.getItem('bciis_concerns') || '[]');
      const userConcerns = concerns.filter(c => String(c.userId) === String(userId));
      return { success: true, concerns: userConcerns };
    }
  },

  replyConcern: async (data) => {
    try {
      const response = await apiClient.post('/concerns/reply', data);
      return response.data;
    } catch (error) {
      let concerns = JSON.parse(localStorage.getItem('bciis_concerns') || '[]');
      let updatedConcern = null;
      concerns = concerns.map(c => {
        if (c.id === Number(data.concernId)) {
          updatedConcern = {
            ...c,
            status: data.status || 'Resolved',
            adminReply: data.replyMessage,
            repliedAt: new Date().toISOString()
          };
          return updatedConcern;
        }
        return c;
      });
      localStorage.setItem('bciis_concerns', JSON.stringify(concerns));

      // Send user notification
      if (updatedConcern) {
        const storedNotifs = JSON.parse(localStorage.getItem('bciis_notifications') || '[]');
        storedNotifs.unshift({
          id: Date.now(),
          userId: updatedConcern.userId,
          type: 'concern_reply',
          title: `Admin Responded to: ${updatedConcern.subject}`,
          message: `Admin reply: "${data.replyMessage}"`,
          read: false,
          createdAt: new Date().toISOString()
        });
        localStorage.setItem('bciis_notifications', JSON.stringify(storedNotifs));
      }

      return { success: true, concern: updatedConcern, message: 'Reply sent successfully' };
    }
  }
};

// ─────────────────────────────────────────────────────────────
// Visitors API — Website visit tracking
// ─────────────────────────────────────────────────────────────
export const visitorsAPI = {
  /**
   * Record a new visit.
   * @param {{ visitor_token: string, page_url?: string, referrer_url?: string, session_id?: string }} data
   */
  record: async (data) => {
    try {
      const response = await apiClient.post('/visitors/record', data);
      return response.data;
    } catch (err) {
      // Silently fail — visitor tracking should not break the UI
      return { success: false };
    }
  },

  /**
   * Get total visitor counts.
   * @returns {{ total_visits: number, unique_visitors: number }}
   */
  getCount: async () => {
    try {
      const response = await apiClient.get('/visitors/count');
      return response.data?.data || { total_visits: 0, unique_visitors: 0 };
    } catch {
      return { total_visits: 0, unique_visitors: 0 };
    }
  },

  /**
   * Get paginated visitor records (admin use).
   */
  getRecords: async (limit = 50, offset = 0) => {
    try {
      const response = await apiClient.get('/visitors/records', { params: { limit, offset } });
      return response.data?.data || [];
    } catch {
      return [];
    }
  },

  /**
   * Get daily visitor statistics for last 30 days (admin use).
   */
  getDailyStats: async () => {
    try {
      const response = await apiClient.get('/visitors/daily-stats');
      return response.data?.data || [];
    } catch {
      return [];
    }
  },
};

// ─────────────────────────────────────────────────────────────
// Announcements API — Real-time Announcements & Bulletins
// ─────────────────────────────────────────────────────────────
export const announcementsAPI = {
  getFeed: async (userId, role) => {
    try {
      const response = await apiClient.get('/announcements', { params: { userId, role } });
      return response.data;
    } catch (err) {
      console.warn('[announcementsAPI] getFeed Error:', err.message);
      return { success: false, announcements: [] };
    }
  },

  getUnreadCount: async (userId, role) => {
    try {
      const response = await apiClient.get('/announcements/unread-count', { params: { userId, role } });
      return response.data?.unreadCount || 0;
    } catch (err) {
      return 0;
    }
  },

  getAdminAll: async () => {
    try {
      const response = await apiClient.get('/announcements/admin/all');
      return response.data;
    } catch (err) {
      return { success: false, announcements: [] };
    }
  },

  create: async (data) => {
    const response = await apiClient.post('/announcements/create', data);
    return response.data;
  },

  markRead: async (id, userId) => {
    try {
      const response = await apiClient.post(`/announcements/${id}/read`, { userId });
      return response.data;
    } catch (err) {
      return { success: false };
    }
  },

  markAllRead: async (userId, role) => {
    try {
      const response = await apiClient.post('/announcements/read-all', { userId, role });
      return response.data;
    } catch (err) {
      return { success: false };
    }
  },

  update: async (id, data) => {
    const response = await apiClient.put(`/announcements/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/announcements/${id}`);
    return response.data;
  }
};